import { create } from 'zustand';
import axios from 'axios';

// 기존 backend-api URL (admin 기능은 v2 미구현으로 레거시 API 사용)
const LEGACY_API_URL = `${import.meta.env.VITE_SERVER_URL}`;

// Legacy API 인스턴스 - 사용자, 역할, 권한 관리 등 v2 미구현 기능용
const legacyApi = axios.create({
  baseURL: LEGACY_API_URL,
});
legacyApi.interceptors.request.use(
  (config) => {
    const token = JSON.parse(localStorage.getItem('auth-storage'))?.state?.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const useAdminStore = create((set, get) => ({
  // State
  users: [],
  roles: [],
  permissions: [],
  auditLogs: [],
  isLoading: false,
  error: null,

  // === User Management ===

  fetchUsers: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.get('/admin/users');
      set({ users: data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || '사용자 목록 조회 실패', isLoading: false });
    }
  },

  getUser: async (userId) => {
    try {
      const { data } = await legacyApi.get(`/admin/users/${userId}`);
      return data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || '사용자 조회 실패');
    }
  },

  createUser: async (userData) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.post('/admin/users', userData);
      await get().fetchUsers();
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '사용자 생성 실패');
    }
  },

  updateUser: async (userId, userData) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.put(`/admin/users/${userId}`, userData);
      await get().fetchUsers();
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '사용자 수정 실패');
    }
  },

  deleteUser: async (userId) => {
    set({ isLoading: true, error: null });
    try {
      await legacyApi.delete(`/admin/users/${userId}`);
      await get().fetchUsers();
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '사용자 삭제 실패');
    }
  },

  // === Role Management ===

  fetchRoles: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.get('/admin/roles');
      set({ roles: data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || '역할 목록 조회 실패', isLoading: false });
    }
  },

  getRole: async (roleId) => {
    try {
      const { data } = await legacyApi.get(`/admin/roles/${roleId}`);
      return data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || '역할 조회 실패');
    }
  },

  createRole: async (roleData) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.post('/admin/roles', roleData);
      await get().fetchRoles();
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '역할 생성 실패');
    }
  },

  updateRole: async (roleId, roleData) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.put(`/admin/roles/${roleId}`, roleData);
      await get().fetchRoles();
      return data;
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '역할 수정 실패');
    }
  },

  deleteRole: async (roleId) => {
    set({ isLoading: true, error: null });
    try {
      await legacyApi.delete(`/admin/roles/${roleId}`);
      await get().fetchRoles();
    } catch (error) {
      set({ isLoading: false });
      throw new Error(error.response?.data?.detail || '역할 삭제 실패');
    }
  },

  // === Permission Management ===

  fetchPermissions: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.get('/admin/permissions');
      set({ permissions: data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || '권한 목록 조회 실패', isLoading: false });
    }
  },

  // === Audit Logs ===

  fetchAuditLogs: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await legacyApi.get('/admin/audit-logs', { params });
      set({ auditLogs: data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || '감사 로그 조회 실패', isLoading: false });
    }
  },

  // === Clear Error ===

  clearError: () => set({ error: null }),
}));
